import { useMemo, useContext } from 'react';
import { useIntegrationSoftwares } from 'helpers/integrations';
import { useTranslation } from 'react-i18next';
import { ImputationDimensionsContext } from 'routes/BudgetModule/components/BudgetWithProvider/ImputationDimensionsContext';
import { IntegrationContext } from 'routes/BudgetModule/components/BudgetWithProvider/IntegrationContext';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import getImputationsColumns from './getImputationsColumns';
import {
    BudgetProposalT,
    FieldSettingsT,
} from 'routes/BudgetModule/config/types';
import { GridApi } from 'ag-grid-community';
import { usePrimaryColor } from 'styles/theme';
import { isCustomBudgetProposal } from '../../hooks/budgetProposal';
import {
    EXCLUDED_DIMENSIONS_BY_CONTEXT,
    getImputationDimensionsLabelsCodesAndOther,
} from 'routes/BudgetModule/config/hooks';
import { useGetPhases } from 'routes/BudgetModule/shared/phases/hook';

type UseGetImputationsColumnsParams = {
    budgetProposal: BudgetProposalT;
    gridApi: GridApi | null;
    columnsSettings: FieldSettingsT[];
};

export const useGetImputationsColumns = ({
    budgetProposal,
    gridApi,
    columnsSettings,
}: UseGetImputationsColumnsParams) => {
    const imputationRawDimensions = useContext(ImputationDimensionsContext);
    const { data: phasesSettings = [] } = useGetPhases();
    const imputationDimensions = getImputationDimensionsLabelsCodesAndOther(
        imputationRawDimensions,
        [
            ...EXCLUDED_DIMENSIONS_BY_CONTEXT.BudgetProposal,
            ...EXCLUDED_DIMENSIONS_BY_CONTEXT.ExpenseIncome,
        ],
    );

    // -- isSCHOOL -- //
    const integrations = useContext(IntegrationContext);
    const isSchool = isCustomBudgetProposal(integrations);

    const { t } = useTranslation();
    const primaryColor = usePrimaryColor();
    const statusesSettings = useContext(StatusSettingsContext);

    const { data: softwares, isLoading: isIntegrationLoading } =
        useIntegrationSoftwares();

    const shownColumns = useMemo(() => {
        if (isIntegrationLoading || !softwares || !gridApi) {
            return [];
        }
        return getImputationsColumns({
            budgetProposal,
            gridApi,
            imputationDimensions,
            primaryColor,
            statusesSettings,
            phasesSettings,
            t,
            columnsSettings,
            isSchool,
            softwares,
        });
    }, [
        budgetProposal,
        gridApi,
        JSON.stringify(imputationDimensions),
        primaryColor,
        statusesSettings,
        t,
        columnsSettings,
        isSchool,
        isIntegrationLoading,
        JSON.stringify(softwares),
    ]);

    return shownColumns;
};
